import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Activity } from 'lucide-react';
import { dashboardService, TeamActivity } from '@/app/services/dashboardService';
import { cn } from '@/lib/utils';
import { formatDistanceToNow } from 'date-fns';

interface TeamActivityFeedWidgetProps {
  className?: string;
  limit?: number;
}

const TeamActivityFeedWidget: React.FC<TeamActivityFeedWidgetProps> = ({
  className,
  limit = 15,
}) => {
  const { data: activities, isLoading } = useQuery({
    queryKey: ['team-activity', limit],
    queryFn: () => dashboardService.getTeamActivity(limit),
    refetchInterval: 15000,
  });

  const getInitials = (name: string): string => {
    const parts = name.split(' ');
    if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
    return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
  };

  const getRelativeTime = (timestamp: string): string => {
    try {
      return formatDistanceToNow(new Date(timestamp), { addSuffix: true });
    } catch {
      return 'recently';
    }
  };

  if (isLoading) {
    return (
      <Card className={cn("h-full", className)}>
        <CardHeader className="pb-3">
          <Skeleton className="h-4 w-28" />
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="flex items-start gap-3">
                <Skeleton className="h-7 w-7 rounded-full flex-shrink-0" />
                <div className="flex-1">
                  <Skeleton className="h-3 w-full mb-2" />
                  <Skeleton className="h-2.5 w-20" />
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={cn("h-full flex flex-col", className)}>
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
          <Activity className="h-3.5 w-3.5" />
          Team Activity
        </CardTitle>
      </CardHeader>
      <CardContent className="flex-1 overflow-hidden">
        <ScrollArea className="h-[280px] pr-2">
          {activities && activities.length > 0 ? (
            <div className="space-y-0.5">
              {activities.map((activity: TeamActivity) => (
                <div
                  key={activity.id}
                  className="flex items-start gap-3 px-2 py-2.5 rounded-md hover:bg-muted/40 transition-colors"
                >
                  <Avatar className="h-7 w-7 mt-0.5 flex-shrink-0">
                    <AvatarFallback className="text-xs font-medium bg-muted text-muted-foreground">
                      {getInitials(activity.user_name)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm leading-snug">
                      <span className="font-medium">{activity.user_name}</span>
                      <span className="text-muted-foreground"> completed </span>
                      <span className="font-medium">{activity.task_title}</span>
                    </p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {getRelativeTime(activity.timestamp)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-full text-center py-10">
              <Activity className="h-8 w-8 text-muted-foreground/25 mb-3" />
              <p className="text-sm text-muted-foreground">No recent activity</p>
              <p className="text-xs text-muted-foreground/60 mt-0.5">
                Completed tasks will appear here
              </p>
            </div>
          )}
        </ScrollArea>
      </CardContent>
    </Card>
  );
};

export default TeamActivityFeedWidget;
